import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'reactstrap';
import { Utils } from '../../services';
import { resolvePdfLayoutDocument } from '../../services/pdfLayoutSettingsUtils';
import { build_pdf_sample_download_context } from '../../services/pdfLayoutPreviewSample';

import './PdfLayoutSampleButton.scss';

/**
 * Download a PDF sample for a default-layout mapping.
 * Redux list layouts omit pages/widgets (getListingFields), so we resolve the
 * id from the list then GET the full document before preview_pdf.
 */
const PdfLayoutSampleButton = ({
    layoutId,
    storedLayouts,
    lang,
    tickettypeId,
    pricingKey,
    pricing,
}) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);

    const listLayout = resolvePdfLayoutDocument(layoutId, storedLayouts);

    const downloadSample = useCallback(async () => {
        if (!layoutId || !resolvePdfLayoutDocument(layoutId, storedLayouts))
            return;

        setLoading(true);
        try {
            const { default: PdfLayoutsApi } = await import('../../services/rest/PdfLayouts');
            const json = await PdfLayoutsApi.get(layoutId);
            const layout = json?.layout;
            if (!layout?.pages?.length)
                throw new Error('Layout has no pages');

            const previewLang = lang || Utils.default_lang();
            const context = build_pdf_sample_download_context({
                tickettypeId,
                pricingKey,
                pricing,
            });
            const blob = await PdfLayoutsApi.previewPdf(layout, previewLang, context);
            const url = URL.createObjectURL(blob);
            const anchor = document.createElement('a');
            const layoutName = Utils.localized_or_fallback(layout.name, previewLang, layout._id);
            anchor.href = url;
            anchor.download = `${layoutName || 'layout'}.pdf`;
            anchor.click();
            URL.revokeObjectURL(url);
        } catch (e) {
            console.error('Could not download PDF sample', e);
        } finally {
            setLoading(false);
        }
    }, [lang, layoutId, pricing, pricingKey, storedLayouts, tickettypeId]);

    if (!layoutId)
        return null;

    return (
        <Button
            type="button"
            color="secondary"
            size="sm"
            className="PdfLayoutMappings-sample EmailLayoutsField-action"
            disabled={loading || !listLayout}
            onClick={downloadSample}
        >
            {loading ? (
                <i className="fa fa-spinner fa-spin" />
            ) : (
                <span>
                    <i className="fa fa-download" />
                    &nbsp;
                    {t('settings.default_layouts.download_sample')}
                </span>
            )}
        </Button>
    );
};

export default PdfLayoutSampleButton;
